import React from "react";
import { Box, Divider, Switch } from "@mui/joy";
import type { GoogleConfig, ZekayConfig } from "../../../../api/config/Config.typed";
import useAppSettings from "../../../../contexts/app/appSettings";
import { useAlert } from "../../../../contexts/app/notificationToast";
import DarkTypography from "../../../common/DarkTypography";
import DarkInput from "../../../common/DarkInput";
import SettingsSectionCard from "../../../shared/settingsSection/SettingsSectionCard";
import SettingsSectionEditToolbar from "../../../shared/settingsSection/SettingsSectionEditToolbar";

const emptyGoogleToken = (): GoogleConfig["GoogleToken"] => ({
  access_token: "",
  token_type: "",
  refresh_token: "",
  expiry: "",
});

const emptyGoogleWeb = () => ({
  client_id: "",
  project_id: "",
  auth_uri: "",
  token_uri: "",
  client_secret: "",
  redirect_uris: [] as string[],
});

export type GoogleSectionDraftState = {
  manageGroups: boolean;
  token: GoogleConfig["GoogleToken"];
  clientId: string;
  clientSecret: string;
  redirectUrisCsv: string;
  authUri: string;
  tokenUri: string;
  projectId: string;
};

function buildDraft(prev: ZekayConfig): GoogleSectionDraftState {
  const tk = prev.GoogleConfig?.GoogleToken || emptyGoogleToken();
  const web = prev.GoogleConfig?.GoogleCredentials?.web || emptyGoogleWeb();

  return {
    manageGroups: prev.ManageGroups,
    token: { ...tk },
    clientId: web.client_id || "",
    clientSecret: web.client_secret || "",
    redirectUrisCsv: (web.redirect_uris || []).join(", "),
    authUri: web.auth_uri || "",
    tokenUri: web.token_uri || "",
    projectId: web.project_id || "",
  };
}

function applyDraftToPrev(prev: ZekayConfig, draft: GoogleSectionDraftState): ZekayConfig {
  const webPrev = prev.GoogleConfig?.GoogleCredentials?.web || emptyGoogleWeb();
  const redirect_list = draft.redirectUrisCsv
    .split(",")
    .map((s) => s.trim())
    .filter((s) => s.length > 0);

  const webMerged = {
    ...webPrev,
    client_id: draft.clientId,
    client_secret: draft.clientSecret,
    auth_uri: draft.authUri,
    token_uri: draft.tokenUri,
    project_id: draft.projectId,
    redirect_uris: redirect_list,
  };

  return {
    ...prev,
    ManageGroups: draft.manageGroups,
    GoogleConfig: {
      ...(prev.GoogleConfig || {}),
      GoogleToken: { ...draft.token },
      GoogleCredentials: { web: webMerged },
    },
  } as ZekayConfig;
}

type Props = {
  config: ZekayConfig;
  canEditBackend: boolean;
};

export default function GoogleGroupsConfigSection({ config, canEditBackend }: Props) {
  const { saveConfig } = useAppSettings();
  const { showAlert } = useAlert();

  const [editing, setEditing] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [draft, setDraft] = React.useState<GoogleSectionDraftState | null>(null);

  const startEdit = () => {
    setDraft(buildDraft(config));
    setEditing(true);
  };

  const cancelEdit = () => {
    setDraft(null);
    setEditing(false);
  };

  const save = React.useCallback(async () => {
    if (draft === null || saving) return;

    const nextMerged = applyDraftToPrev(config, draft);
    setSaving(true);
    try {
      await saveConfig(nextMerged);
      showAlert("Enregistré", "success");
      cancelEdit();
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Échec de l'enregistrement";
      showAlert(msg, "danger");
    } finally {
      setSaving(false);
    }
  }, [config, draft, saveConfig, showAlert, saving]);

  const d = editing && draft !== null ? draft : buildDraft(config);
  const fieldsEnabled = editing && canEditBackend;
  const googleDetailsEnabled = editing && d.manageGroups;
  const innerFieldsEnabled = fieldsEnabled && d.manageGroups;

  return (
    <SettingsSectionCard
      title="Synchronisation"
      titleEnd={
        <SettingsSectionEditToolbar
          canEditBackend={canEditBackend}
          isEditing={editing}
          saving={saving}
          onEdit={startEdit}
          onCancel={cancelEdit}
          onSave={() => void save()}
        />
      }
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: googleDetailsEnabled ? 2 : 0 }}>
        <DarkTypography
          component="div"
          level="body-md"
          sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}
        >
          Mise à jour automatique des groupes Google
          <Switch
            checked={d.manageGroups}
            onChange={(e) => {
              if (!fieldsEnabled) return;
              setDraft((prev) =>
                prev !== null ? { ...prev, manageGroups: e.target.checked } : prev,
              );
            }}
            disabled={!fieldsEnabled}
          />
        </DarkTypography>
      </Box>

      {googleDetailsEnabled ? (
        <>
          <Divider sx={{ my: 2 }} />

          <DarkTypography level="body-sm" colorVariant="muted" sx={{ mb: 1 }}>
            Token
          </DarkTypography>
          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2, flexWrap: "wrap", mb: 2 }}>
            <Box
              sx={{
                flex: "1 1 280px",
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "140px 1fr" },
                gap: 2,
                minWidth: 0,
                width: "100%",
              }}
            >
              <DarkTypography level="body-md" colorVariant="primary">
                Access token
              </DarkTypography>
              <DarkInput
                placeholder="access_token"
                value={d.token.access_token}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setDraft((prev) =>
                    prev !== null
                      ? { ...prev, token: { ...prev.token, access_token: e.target.value } }
                      : prev,
                  )
                }
                disabled={!innerFieldsEnabled}
              />
              <DarkTypography level="body-md" colorVariant="primary">
                Type
              </DarkTypography>
              <DarkInput
                placeholder="token_type"
                value={d.token.token_type}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setDraft((prev) =>
                    prev !== null
                      ? { ...prev, token: { ...prev.token, token_type: e.target.value } }
                      : prev,
                  )
                }
                disabled={!innerFieldsEnabled}
              />
              <DarkTypography level="body-md" colorVariant="primary">
                Refresh token
              </DarkTypography>
              <DarkInput
                placeholder="refresh_token"
                value={d.token.refresh_token}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setDraft((prev) =>
                    prev !== null
                      ? { ...prev, token: { ...prev.token, refresh_token: e.target.value } }
                      : prev,
                  )
                }
                disabled={!innerFieldsEnabled}
              />
              <DarkTypography level="body-md" colorVariant="primary">
                Expiry (ISO)
              </DarkTypography>
              <DarkInput
                placeholder="expiry (ISO)"
                value={d.token.expiry}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setDraft((prev) =>
                    prev !== null
                      ? { ...prev, token: { ...prev.token, expiry: e.target.value } }
                      : prev,
                  )
                }
                disabled={!innerFieldsEnabled}
              />
            </Box>
          </Box>

          <Divider sx={{ my: 2 }} />

          <DarkTypography level="body-sm" colorVariant="muted" sx={{ mb: 1 }}>
            OAuth client
          </DarkTypography>
          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2, flexWrap: "wrap" }}>
            <Box
              sx={{
                flex: "1 1 280px",
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "140px 1fr" },
                gap: 2,
                minWidth: 0,
                width: "100%",
              }}
            >
              <DarkTypography level="body-md" colorVariant="primary">
                Client ID
              </DarkTypography>
              <DarkInput
                placeholder="client_id"
                value={d.clientId}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setDraft((prev) => (prev !== null ? { ...prev, clientId: e.target.value } : prev))
                }
                disabled={!innerFieldsEnabled}
              />
              <DarkTypography level="body-md" colorVariant="primary">
                Client secret
              </DarkTypography>
              <DarkInput
                placeholder="client_secret"
                value={d.clientSecret}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setDraft((prev) =>
                    prev !== null ? { ...prev, clientSecret: e.target.value } : prev,
                  )
                }
                disabled={!innerFieldsEnabled}
              />
              <DarkTypography level="body-md" colorVariant="primary">
                Redirect URIs
              </DarkTypography>
              <DarkInput
                placeholder="uri1, uri2, ..."
                value={d.redirectUrisCsv}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setDraft((prev) =>
                    prev !== null ? { ...prev, redirectUrisCsv: e.target.value } : prev,
                  )
                }
                disabled={!innerFieldsEnabled}
              />
              <DarkTypography level="body-md" colorVariant="primary">
                Auth URI
              </DarkTypography>
              <DarkInput
                placeholder="auth_uri"
                value={d.authUri}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setDraft((prev) => (prev !== null ? { ...prev, authUri: e.target.value } : prev))
                }
                disabled={!innerFieldsEnabled}
              />
              <DarkTypography level="body-md" colorVariant="primary">
                Token URI
              </DarkTypography>
              <DarkInput
                placeholder="token_uri"
                value={d.tokenUri}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setDraft((prev) => (prev !== null ? { ...prev, tokenUri: e.target.value } : prev))
                }
                disabled={!innerFieldsEnabled}
              />
              <DarkTypography level="body-md" colorVariant="primary">
                Project ID
              </DarkTypography>
              <DarkInput
                placeholder="project_id"
                value={d.projectId}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setDraft((prev) =>
                    prev !== null ? { ...prev, projectId: e.target.value } : prev,
                  )
                }
                disabled={!innerFieldsEnabled}
              />
            </Box>
          </Box>
        </>
      ) : null}
    </SettingsSectionCard>
  );
}
